import { activeLang, useT } from '../lib/i18n';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router';
import { LOCK_PASSWORD_MIN_LENGTH, type OwnerEventDto, type OwnerStatusDto } from '@bingo/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PasswordInput } from './PasswordInput';

const DETAIL_LABELS: Record<string, string> = {
  created: 'created', updated: 'updated', unchanged: 'unchanged', keptTeamEdits: 'team edits kept',
  productsChanged: 'platform products copied', missing: 'missing', bookings: 'bookings', seconds: 'seconds', done: 'done',
};

const ACTION_LABELS: Record<string, string> = {
  lock_password_set: 'Lock password chosen',
  lock_password_changed: 'Lock password changed',
  locked: 'App locked',
  unlocked: 'App unlocked',
  wrong_lock_password: 'Wrong lock password',
  wrong_lock_password_blocked: 'Wrong lock password: blocked 15 minutes',
  reset_from_server: 'Lock reset from the server settings',
  lock_reset_email_sent: 'Reset e-mail sent',
  lock_password_reset_by_email: 'Lock password reset from the e-mail link',
  history_file_checked: 'App 2026 file checked',
  history_imported: 'App 2026 history imported',
  watch_run: 'Lost-booking watch run by hand',
  history_import_failed: 'App 2026 import stopped',
  integration_created: 'n8n key created',
  integration_revoked: 'n8n key revoked',
};

/** Owner area in My account: lock password, lock and unlock, private history. */
export function OwnerPanel({ status }: { status: OwnerStatusDto }) {
  const { refreshMe } = useAuth();
  const [lockPassword, setLockPassword] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [currentLock, setCurrentLock] = useState('');
  const [newLock, setNewLock] = useState('');
  const [showChange, setShowChange] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'good' | 'bad'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<OwnerEventDto[]>([]);
  const [params, setParams] = useSearchParams();
  const resetToken = params.get('lockReset');
  const t = useT();

  const loadHistory = () => api<{ history: OwnerEventDto[] }>('/api/owner/history').then((r) => setHistory(r.history)).catch(() => undefined);
  useEffect(() => {
    void loadHistory();
  }, []);

  async function run(work: () => Promise<unknown>, done: string) {
    setMsg(null);
    setBusy(true);
    try {
      await work();
      if (done) setMsg({ kind: 'good', text: done });
      await refreshMe();
    } catch (err) {
      setMsg({ kind: 'bad', text: err instanceof ApiError ? err.message : t('Something went wrong.') });
    } finally {
      setBusy(false);
      void loadHistory();
    }
  }

  function savePassword(e: FormEvent) {
    e.preventDefault();
    if (newLock.length < LOCK_PASSWORD_MIN_LENGTH) {
      return setMsg({ kind: 'bad', text: t('Use at least {n} characters.', { n: LOCK_PASSWORD_MIN_LENGTH }) });
    }
    void run(
      () => api('/api/owner/lock-password', {
        method: 'POST',
        body: { loginPassword, currentLockPassword: status.hasLockPassword ? currentLock : undefined, newLockPassword: newLock },
      }),
      t('Lock password saved. Keep it somewhere safe: nobody else can unlock the app.'),
    ).then(() => {
      setLoginPassword('');
      setCurrentLock('');
      setNewLock('');
      setShowChange(false);
    });
  }

  function forgot() {
    void run(
      async () => {
        const r = await api<{ sentTo: string }>('/api/owner/lock-password/forgot', { method: 'POST' });
        setMsg({ kind: 'good', text: t('E-mail sent to {to}. The link works for 30 minutes.', { to: r.sentTo }) });
      },
      '',
    );
  }

  function resetFromEmail(e: FormEvent) {
    e.preventDefault();
    if (newLock.length < LOCK_PASSWORD_MIN_LENGTH) {
      return setMsg({ kind: 'bad', text: t('Use at least {n} characters.', { n: LOCK_PASSWORD_MIN_LENGTH }) });
    }
    void run(
      () => api('/api/owner/lock-password/reset', { method: 'POST', body: { token: resetToken, loginPassword, newLockPassword: newLock } }),
      t('New lock password saved.'),
    ).then(() => {
      setLoginPassword('');
      setNewLock('');
      setParams({}, { replace: true });
    });
  }

  function toggleLock(e: FormEvent) {
    e.preventDefault();
    const action = status.locked ? 'unlock' : 'lock';
    void run(
      () => api(`/api/owner/${action}`, { method: 'POST', body: { lockPassword } }),
      status.locked ? t('The app is unlocked. Everyone can work again.') : t('The app is locked. Only you can use it now.'),
    ).then(() => setLockPassword(''));
  }

  const passwordForm = (
    <form className="stack" onSubmit={savePassword}>
      <div className="field">
        <label htmlFor="own-login">{t('Password you use to log in')}</label>
        <PasswordInput id="own-login" className="input" autoComplete="current-password" required value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} />
      </div>
      {status.hasLockPassword && (
        <div className="field">
          <label htmlFor="own-cur">{t('Current lock password')}</label>
          <PasswordInput id="own-cur" className="input" autoComplete="off" required value={currentLock} onChange={(e) => setCurrentLock(e.target.value)} />
        </div>
      )}
      <div className="field">
        <label htmlFor="own-new">{t('New lock password (only for locking; different from your log in password)')}</label>
        <PasswordInput id="own-new" className="input" autoComplete="new-password" required value={newLock} onChange={(e) => setNewLock(e.target.value)} />
      </div>
      <div><button className="btn btn-primary" disabled={busy}>{t('Save lock password')}</button></div>
    </form>
  );

  return (
    <div className="card section">
      <h2>{t('Master lock')}</h2>
      <span className="muted" style={{ fontSize: 13 }}>{t('Only you see this. Locking stops everyone else at once; only you can unlock.')}</span>
      {status.resetSwitchOn && (
        <div className="alert alert-bad">{t('The emergency reset is on in the server settings (OWNER_LOCK_RESET). Turn it off, then choose a new lock password.')}</div>
      )}
      {msg && <div className={`alert alert-${msg.kind}`}>{msg.text}</div>}

      {resetToken ? (
        <form className="stack" onSubmit={resetFromEmail}>
          <b>{t('Choose a new lock password (link from the e-mail).')}</b>
          <div className="field">
            <label htmlFor="rst-login">{t('Password you use to log in')}</label>
            <PasswordInput id="rst-login" className="input" autoComplete="current-password" required value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="rst-new">{t('New lock password (only for locking; different from your log in password)')}</label>
            <PasswordInput id="rst-new" className="input" autoComplete="new-password" required value={newLock} onChange={(e) => setNewLock(e.target.value)} />
          </div>
          <div><button className="btn btn-primary" disabled={busy}>{t('Save new lock password')}</button></div>
        </form>
      ) : !status.hasLockPassword ? (
        <>
          <b>{t('First, choose a lock password.')}</b>
          {passwordForm}
        </>
      ) : (
        <>
          <div>
            {t('Status:')} {status.locked
              ? <span className="badge badge-bad">{status.lockedAt ? t('Locked since {when}', { when: new Date(status.lockedAt).toLocaleString(activeLang === 'fr' ? 'fr-FR' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' }) }) : t('Locked')}</span>
              : <span className="badge badge-good">{t('Open')}</span>}
          </div>
          <form className="stack" onSubmit={toggleLock}>
            <div className="field">
              <label htmlFor="own-lock">{t('Lock password (not your log in password)')}</label>
              <PasswordInput id="own-lock" className="input" autoComplete="off" required value={lockPassword} onChange={(e) => setLockPassword(e.target.value)} />
            </div>
            <div>
              <button className={status.locked ? 'btn btn-primary' : 'btn btn-danger'} disabled={busy}>
                {status.locked ? t('Unlock the app') : t('Lock the app for everyone')}
              </button>
            </div>
          </form>
          {showChange ? passwordForm : (
            <div className="row-actions">
              <button className="btn btn-sm" onClick={() => setShowChange(true)}>{t('Change lock password')}</button>
              {status.emailReady && <button className="btn btn-sm" disabled={busy} onClick={forgot}>{t('Forgot the lock password?')}</button>}
            </div>
          )}
        </>
      )}

      <div className="row-actions" style={{ justifyContent: 'flex-start' }}>
        <Link className="btn btn-sm" to="/import-history">{t('Import history from Bingo App 2026')}</Link>
        <Link className="btn btn-sm" to="/integrations">{t('Integrations (n8n)')}</Link>
        <Link className="btn btn-sm" to="/bdr-settings">{t('BDR settings')}</Link>
      </div>

      <h2 style={{ marginTop: 8 }}>{t('Master history')}</h2>
      {history.length === 0 ? <span className="muted">{t('Nothing yet.')}</span> : (
        <div className="owner-history">
          {history.map((h, i) => (
            <div key={i}>
              <time>{new Date(h.at).toLocaleString(activeLang === 'fr' ? 'fr-FR' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' })}</time>
              <span>{ACTION_LABELS[h.action] ? t(ACTION_LABELS[h.action]!) : h.action}</span>
              {Object.keys(h.details ?? {}).length > 0 && (
                <span className="muted">
                  {Object.entries(h.details).map(([k, v]) => `${DETAIL_LABELS[k] ? t(DETAIL_LABELS[k]!) : k}: ${v}`).join(' · ')}
                </span>
              )}
              {h.ip && <span className="muted">{h.ip}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
